package kz.dentalcrm.app.screens.initial_inspection

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.ChevronLeft
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.custom_option_placeholder
import dentalcrm.composeapp.generated.resources.initial_inspection_title
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UICheckBox
import kz.dentalcrm.app.core.ui.components.UISelfTextInput
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import dentalcrm.composeapp.generated.resources.save
import kz.dentalcrm.app.core.ui.components.UIIconBackButton
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun InitialInspectionScreen(
    appointmentId: Int,
    viewModel: InitialInspectionViewModel = koinViewModel()
) {
    val state by viewModel.viewState.collectAsState()
    val event: (InitialInspectionEvent) -> Unit = viewModel::setEvent

    val navigator: AppNavigator = koinInject()

    LaunchedEffect(Unit) {
        viewModel.setEvent(InitialInspectionEvent.LoadData(appointmentId))
    }

    LaunchedEffect(viewModel.effect) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is InitialInspectionEffect.NavigateBack -> navigator.navigateUp()
                is InitialInspectionEffect.ShowError -> {
                    // Handle error
                }
            }
        }
    }

    UiStateScaffold(
        modifier = Modifier.fillMaxSize(),
        isLoading = state.isLoading
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
                .statusBarsPadding()
        ) {
            ContentHeader(
                modifier = Modifier.fillMaxWidth()
                    .padding(horizontal = 12.dp),
                text = stringResource(Res.string.initial_inspection_title),
                leadingContent = {
                    UIIconBackButton(
                        onClick = {
                            navigator.navigateUp()
                        }
                    )
                }
            )

            LazyColumn(
                modifier = Modifier.fillMaxWidth()
                    .weight(1f)
                    .padding(horizontal = 16.dp)
            ) {
                state.inspectionTypes.forEachIndexed { index, typeState ->
                    item(key = "header_${typeState.id}") {
                        Spacer(modifier = Modifier.height(if (index == 0) 16.dp else 24.dp))
                        Text(
                            text = "${index + 1}. ${typeState.name}",
                            style = DefaultTheme.typography.bodyLargeMedium,
                            color = DefaultTheme.colors.onSurface
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                    }

                    items(
                        items = typeState.options,
                        key = { "option_${typeState.id}_${it.option.id}" }
                    ) { optionState ->
                        UICheckBox(
                            modifier = Modifier.fillMaxWidth(),
                            text = optionState.option.value,
                            checked = optionState.isChecked,
                            enabled = !optionState.isLoading,
                            onCheckedChange = {
                                event(
                                    InitialInspectionEvent.ToggleOption(
                                        typeId = typeState.id,
                                        optionId = optionState.option.id,
                                        currentChecked = optionState.isChecked
                                    )
                                )
                            }
                        )
                    }

                    item(key = "custom_input_${typeState.id}") {
                        Spacer(modifier = Modifier.height(8.dp))
                        UISelfTextInput(
                            modifier = Modifier.fillMaxWidth(),
                            label = stringResource(Res.string.custom_option_placeholder),
                            value = typeState.customInput.value,
                            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done).copy(
                                keyboardType = KeyboardType.Text
                            ),
                            valueChanged = { newValue ->
                                event(
                                    InitialInspectionEvent.CustomInputChanged(
                                        typeId = typeState.id,
                                        value = newValue
                                    )
                                )
                            }
                        )
                    }
                }

                item("footer") {
                    Box(
                        modifier = Modifier.height(56.dp)
                            .navigationBarsPadding()
                    )
                }
            }

            if (state.inspectionTypes.any { it.customInput.value.isNotBlank() }) {
                UIButton(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp)
                        .padding(bottom = 16.dp)
                        .navigationBarsPadding(),
                    text = stringResource(Res.string.save),
                    onClick = { event(InitialInspectionEvent.SaveCustomOptions) },
                    isLoading = state.isSavingCustom
                )
            }
        }
    }
}
